/**
 * Best-effort launcher for the `denker-app://` deep link from the browser.
 *
 * Why this exists (Z-1279):
 *
 * Chromium-based browsers cache "this scheme has no handler" *per document*.
 * If the user clicks "Open Denker" before installing the desktop app, that
 * click queries macOS Launch Services, gets "no handler", and Chrome
 * remembers the failure for the lifetime of the document. After the user
 * installs the app, subsequent clicks on the same tab silently no-op
 * because Chrome never re-queries Launch Services. The only thing that
 * forces a re-query is a full document reload (F5 / cmd-R).
 *
 * macOS Launch Services itself also lazy-scans `/Applications` — a freshly
 * dragged-in bundle may not be registered with LS until Finder/Spotlight
 * touches it, compounding the problem.
 *
 * What we do:
 *
 * 1. Navigate to the deep link.
 * 2. Listen for the tab losing visibility (`visibilitychange` →
 *    `document.hidden`). A successful scheme launch causes the OS to
 *    swap focus to the app, which fires this within a few hundred ms.
 * 3. If we're still visible after `FAILURE_TIMEOUT_MS`, assume the click
 *    silently failed (cached "unknown scheme" or LS not yet aware), set a
 *    one-shot sessionStorage marker, and force a reload. The reload flushes
 *    Chrome's per-document scheme cache; the next click works.
 * 4. The marker prevents an infinite reload loop: if a click after reload
 *    still fails (e.g. the app genuinely isn't installed), we no-op so the
 *    user can use the Download button.
 *
 * This is a pure UX recovery — it can't *make* an uninstalled app launch,
 * but it removes the silent-failure footgun for the common case where the
 * app is installed but Chrome cached a pre-install failure.
 */

// How long to wait for the browser/OS to hand control to the desktop app
// before treating the click as failed.
//
// 2500ms gives the user enough time to interact with macOS's first-time
// "Do you want to allow [browser] to open Denker?" prompt without the
// auto-reload firing mid-decision and dismissing it. A successful launch
// still typically hides the tab within ~200ms, so the extra second only
// costs the genuine-failure path, where the user is already waiting.
const FAILURE_TIMEOUT_MS = 2_500;

const AUTORELOAD_KEY = "denker.deep-link-autoreload-fired";

function hasAutoReloadedThisSession(): boolean {
  try {
    return sessionStorage.getItem(AUTORELOAD_KEY) === "1";
  } catch {
    return false;
  }
}

function markAutoReloaded(): void {
  try {
    sessionStorage.setItem(AUTORELOAD_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Clear the auto-reload marker. Call after a user-driven success signal
 *  (e.g. the user clicked the button and the tab actually went hidden)
 *  so a *later* click that fails can retry the reload once. */
function clearAutoReloaded(): void {
  try {
    sessionStorage.removeItem(AUTORELOAD_KEY);
  } catch {
    /* ignore */
  }
}

export type DesktopLaunchOutcome = "launched" | "failed-reloading" | "failed";

export interface LaunchDesktopAppOptions {
  /** Override the failure timeout (mainly for tests). */
  timeoutMs?: number;
  /** Injectable reload, also for tests. */
  reload?: () => void;
  /** Injectable navigate, also for tests. */
  navigate?: (url: string) => void;
  /** Called exactly once with the launch outcome. "failed-reloading" is
   *  invoked synchronously BEFORE the page reload so the caller can flush
   *  analytics (use a beacon transport). "failed" means the one-shot
   *  reload already happened and the tab still never went hidden. That is
   *  a WEAK signal: Denker is a transparent overlay app, so a successful
   *  launch of an already-installed app routinely leaves this tab visible.
   *  Callers must not present "failed" as "not installed" — use neutral
   *  retry/download guidance instead. */
  onOutcome?: (outcome: DesktopLaunchOutcome) => void;
}

/**
 * Launch `url` (a `denker-app://...` URL) and auto-reload the page once
 * if the launch silently fails.
 *
 * Returns a cleanup function the caller can invoke to cancel the failure
 * watcher (e.g. on unmount). Calling cleanup has no effect once the
 * outcome has been resolved.
 */
export function launchDesktopApp(
  url: string,
  options: LaunchDesktopAppOptions = {},
): () => void {
  const timeoutMs = options.timeoutMs ?? FAILURE_TIMEOUT_MS;
  const reload = options.reload ?? (() => window.location.reload());
  const navigate =
    options.navigate ?? ((u: string) => (window.location.href = u));

  let resolved = false;
  let timerId: number | null = null;

  // Only listen on `visibilitychange` (gated on `document.hidden`) and
  // `pagehide`. We intentionally do NOT listen on `window.blur`:
  //   - `blur` fires for ANY focus loss — DevTools docking/undocking, an
  //     OS notification banner, a browser extension popup, another tab
  //     stealing focus, the OS first-time "allow open?" prompt. Treating
  //     all of those as "the desktop app launched" would silently clear
  //     the recovery marker and re-introduce the very bug this launcher
  //     exists to fix.
  //   - A real successful scheme launch on macOS reliably hides the tab,
  //     so `visibilitychange + document.hidden` is the authoritative
  //     signal.
  const cleanup = () => {
    if (resolved) return;
    resolved = true;
    if (timerId !== null) {
      window.clearTimeout(timerId);
      timerId = null;
    }
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  };

  function onSuccess() {
    if (resolved) return;
    // The tab went hidden / unloaded — the OS handed control to the
    // desktop app. Clear the auto-reload marker so a later failed click
    // (same session) can still trigger a one-shot reload.
    clearAutoReloaded();
    cleanup();
    options.onOutcome?.("launched");
  }

  function onVisibilityChange() {
    if (document.hidden) onSuccess();
  }
  function onPageHide() {
    onSuccess();
  }

  function onFailure() {
    if (resolved) return;
    resolved = true;
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
    if (hasAutoReloadedThisSession()) {
      // Already reloaded once this session and the tab still never went
      // hidden. Report it and let the caller drive the recovery UX
      // instead of looping — but see the onOutcome doc: this is a weak
      // signal, not proof the app is missing.
      options.onOutcome?.("failed");
      return;
    }
    markAutoReloaded();
    options.onOutcome?.("failed-reloading");
    reload();
  }

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);
  timerId = window.setTimeout(onFailure, timeoutMs);

  // Kick off the navigation last so all listeners are attached first.
  navigate(url);

  return cleanup;
}
